import { fetchJson } from './api_helpers';
import { APP_CONFIG, BASE_COLUMNS } from '../constants';
import { API_BASE_URL } from '../constants';
import { convertToDateRangeFilter } from '../utils/helpers';
import { toOpenablePostUrl } from '../utils/recentPosts';

export const getEntities = async (options = {}) => {
  const {
    limit = APP_CONFIG.PAGE_SIZE,
    offset = 0,
    sortBy = 'CreatedDate',
    sortOrder = 'desc',
    filters = {},
    columns = [],
    signal = null
  } = options;

  const columnsToSend = Array.from(new Set([...BASE_COLUMNS, ...columns]));
  const processedFilters = { ...filters };

  Object.keys(processedFilters).forEach((columnKey) => {
    const filter = processedFilters[columnKey];
    if (filter && typeof filter === 'object' && (filter.fromdate || filter.todate)) {
      processedFilters[columnKey] = convertToDateRangeFilter(filter.fromdate, filter.todate);
    }
  });

  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  if (Object.keys(processedFilters).length > 0) {
    params.append('filters', JSON.stringify(processedFilters));
  }

  if (columnsToSend.length > 0) {
    params.append('columns', columnsToSend.join(','));
  }

  try {
    const url = `${API_BASE_URL}/awatch/entities?${params}`;
    return await fetchJson(url, { signal });
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }
    throw new Error(`Failed to load entities: ${error.message}`);
  }
};

/**
 * cw_server caps `limit` at 200 (MAX_LIMIT in request_validators.py), so a
 * whole date range has to be walked a page at a time.
 */
const MAX_PAGE_LIMIT = 200;

/**
 * Collect every entity created inside a date range that has an openable post
 * link, newest first. Used by the bulk report action, which needs the links to
 * open and the ids to mark as reported.
 * @param {Object} options
 * @param {string} options.fromdate - Start of the range, YYYY-MM-DD
 * @param {string} options.todate - End of the range, YYYY-MM-DD (inclusive)
 * @param {number} options.maxPosts - Stop collecting once this many are found
 * @param {AbortSignal} options.signal - Optional cancellation signal
 * @returns {Promise<{posts: Array<{entityId: string, url: string}>, total: number}>}
 *   `total` is how many entities the range holds, link or not.
 */
export const getPostsForDateRange = async ({
  fromdate,
  todate,
  maxPosts = 100,
  signal = null,
} = {}) => {
  const posts = [];
  const seen = new Set();
  let offset = 0;
  let total = 0;
  let reachedEnd = false;

  while (posts.length < maxPosts && !reachedEnd) {
    // eslint-disable-next-line no-await-in-loop
    const data = await getEntities({
      limit: MAX_PAGE_LIMIT,
      offset,
      sortBy: 'CreatedDate',
      sortOrder: 'desc',
      filters: { CreatedDate: { fromdate, todate } },
      columns: ['Url'],
      signal,
    });

    const batch = Array.isArray(data?.entities) ? data.entities : [];
    total = Number.isFinite(data?.total) ? data.total : offset + batch.length;
    offset += batch.length;

    batch.forEach((entity) => {
      const url = toOpenablePostUrl(entity?.Url);
      if (url && !seen.has(url)) {
        seen.add(url);
        posts.push({ entityId: String(entity.EntityId), url });
      }
    });

    reachedEnd = batch.length === 0 || offset >= total;
  }

  return { posts: posts.slice(0, maxPosts), total };
};

export const getMoreFiltersData = async (options = {}) => {
  const {
    limit = APP_CONFIG.PAGE_SIZE,
    offset = 0,
    sortBy = 'CreatedDate',
    sortOrder = 'desc',
    filters = {},
    columns = [],
    signal = null
  } = options;

  const processedFilters = { ...filters };
  Object.keys(processedFilters).forEach((columnKey) => {
    const filter = processedFilters[columnKey];
    if (filter && typeof filter === 'object' && (filter.fromdate || filter.todate)) {
      processedFilters[columnKey] = convertToDateRangeFilter(filter.fromdate, filter.todate);
    }
  });

  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  if (Object.keys(processedFilters).length > 0) {
    params.append('filters', JSON.stringify(processedFilters));
  }

  if (columns.length > 0) {
    params.append('columns', columns.join(','));
  }

  try {
    const url = `${API_BASE_URL}/awatch/entities?${params}`;
    return await fetchJson(url, { signal });
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error;
    }
    throw new Error(`Failed to load filter metadata: ${error.message}`);
  }
};

export const getEntityConfiguration = async () => {
  try {
    return await fetchJson(`${API_BASE_URL}/awatch/entities/configuration`);
  } catch (error) {
    throw new Error(`Failed to load entity configuration: ${error.message}`);
  }
};
